/** The Puzzle Map: what the obelisks have uncovered, and where to dig.
 *
 * ## Why this screen exists
 *
 * The obelisks were counting themselves and saying so - "obelisk:3/8" - and
 * that was the whole of it: a number with nothing behind it. In the classic
 * obelisks are the *only* way to find the Grail, because each one takes a piece
 * off a jigsaw laid over a picture of the map, and the ground that is still
 * covered at the end is where to dig. Without the picture the obelisks are
 * scenery and the Grail is unfindable except by digging the map at a day a cell.
 *
 * ## The picture comes from the kernel, and it has to
 *
 * The obvious build is the one that was written first and was wrong: paint the
 * minimap from the observation and lay the jigsaw over it. A
 * `PlayerObservation` masks the terrain of every unseen cell to zero, so on day
 * two the ground under an uncovered piece is fog - and the screen stayed black
 * after an obelisk was visited, which is what it looked like from the outside:
 * a feature that did not work.
 *
 * What an obelisk buys is a look at land the player has NOT walked. That is a
 * rule, and it belongs in `adventure/grail.ts` where it can be read, not in a
 * screen quietly ignoring `visibility`. So `PuzzleView.terrain` arrives with the
 * revealed cells filled in and `PUZZLE_HIDDEN` everywhere else, and this file
 * paints exactly what it is handed. It decides nothing (I10) and it cannot leak
 * more than the puzzle has uncovered even by accident, because it is never
 * given more.
 *
 * ## The covered pieces are drawn as slate, not as a blur
 *
 * A frosted or darkened piece still shows the terrain under it, which would
 * hand the player the answer through the cover. A covered piece is opaque, in
 * the same slate the minimap uses for unexplored ground, with a hairline
 * between pieces so the jigsaw reads as pieces rather than as one dark blob.
 */

import { useEffect, useMemo, useRef } from "react";

import { TERRAINS } from "@game/core";
import { TERRAIN_MINIMAP_COLOUR } from "../adventure/minimap-paint.js";
import { usePhoneSheetFit } from "../theme/phone.js";
import { usePolishStyles } from "./polish.js";
import { ScreenStyles } from "./primitives.js";

/** Everything the kernel decided. See `adventure/grail.ts`. */
export interface PuzzleMapView {
  readonly seen: number;
  readonly total: number;
  readonly cols: number;
  readonly rows: number;
  /** One byte a piece, 1 where the picture shows through. */
  readonly revealed: Uint8Array;
  readonly width: number;
  readonly height: number;
  /** One terrain index per surface cell, `PUZZLE_HIDDEN` where nothing shows. */
  readonly terrain: Uint8Array;
  /** Where to dig, or -1 while any obelisk is still unvisited. */
  readonly cell: number;
  readonly x: number;
  readonly y: number;
}

export interface PuzzleMapProps {
  readonly view: PuzzleMapView;
  /** Centre the adventure camera on the dig site. Only offered once solved. */
  readonly onGoTo?: ((x: number, y: number, z: number) => void) | undefined;
  readonly onClose: () => void;
}

/**
 * The cover, as a triple because it goes down per cell alongside the terrain.
 *
 * Deliberately NOT the minimap's unexplored slate any more. It was, and that
 * was half of why an uncovered piece was invisible: fogged ground and a covered
 * piece were the same colour, so taking a piece off changed nothing on screen.
 * This is a warmer, lighter stone - a piece of a jigsaw lying face down, which
 * is what it is - and no terrain colour is near it.
 */
const COVER_RGB: readonly [number, number, number] = [58, 52, 44];
const COVER_EDGE = "rgba(0,0,0,0.55)";
/** `PuzzleView.terrain`'s "nothing shows here". Mirrors `PUZZLE_HIDDEN`. */
const HIDDEN = 0xff;
const MARK = "#f4e0a3";
const MARK_SHADOW = "rgba(0,0,0,0.8)";

/** The picture is drawn at one texel a cell and blown up with smoothing off. */
const MAX_EDGE = 720;

/**
 * The picture, one texel a cell, as plain pixels.
 *
 * Split out of the component and exported because it is the half of this screen
 * that can be wrong in a way nobody sees: the bug that started all this was an
 * uncovered piece drawing fog, and "the canvas has pixels in it" passes just as
 * happily on a black square as on a map. A pure function can be asked the
 * question that matters - is the ground under an uncovered piece TERRAIN, and
 * is everything else the cover - without a browser.
 *
 * Every cell is opaque. A hidden one takes the cover colour here rather than
 * being left transparent and painted over later, so the image has no holes in
 * it at any point and a piece boundary cannot show a seam.
 */
export function puzzlePixels(view: PuzzleMapView): Uint8ClampedArray<ArrayBuffer> {
  const n = view.width * view.height;
  const out = new Uint8ClampedArray(new ArrayBuffer(n * 4));
  for (let i = 0; i < n; i++) {
    const t = view.terrain[i] ?? HIDDEN;
    const o = i * 4;
    const rgb = t === HIDDEN
      ? COVER_RGB
      : TERRAIN_MINIMAP_COLOUR[TERRAINS[t] ?? "rock"] ?? COVER_RGB;
    out[o] = rgb[0]; out[o + 1] = rgb[1]; out[o + 2] = rgb[2]; out[o + 3] = 255;
  }
  return out;
}

export function PuzzleMap(props: PuzzleMapProps): JSX.Element {
  usePolishStyles();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { view } = props;

  const size = useMemo(() => {
    const w = view.width, h = view.height;
    const scale = Math.max(1, Math.floor(MAX_EDGE / Math.max(w, h)));
    return { w, h, scale, drawnW: w * scale, drawnH: h * scale };
  }, [view.width, view.height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;

    // The picture, at one texel a cell, then blown up. Painting straight into
    // the big canvas would cost width*height*scale^2 writes for the same image.
    //
    // Every cell is opaque: a hidden one takes the cover colour here rather
    // than being left transparent and painted over below, so the canvas has no
    // holes in it at any point and a piece boundary cannot show a seam.
    const cells = document.createElement("canvas");
    cells.width = size.w;
    cells.height = size.h;
    const cellCtx = cells.getContext("2d");
    if (cellCtx === null) return;
    const buf = puzzlePixels(view);
    cellCtx.putImageData(new ImageData(buf, size.w, size.h), 0, 0);

    canvas.width = size.drawnW;
    canvas.height = size.drawnH;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, size.drawnW, size.drawnH);
    ctx.drawImage(cells, 0, 0, size.drawnW, size.drawnH);

    /**
     * The jigsaw, cut where the KERNEL cuts it.
     *
     * `puzzlePieceOf` puts cell `x` in piece `floor(x * cols / width)`, so the
     * cells of piece `p` are `ceil(p * width / cols) .. ceil((p+1) * width /
     * cols) - 1`. Ceil, not floor: on a 36-cell map at 8 columns the piece
     * boundary falls at 4.5, and flooring puts cell 4 outside the rectangle of
     * the piece the kernel says it belongs to. One cell of ground drawn under
     * the wrong piece is a puzzle map that lies, and nothing shows it until
     * somebody digs where it pointed.
     */
    const edgeX = (p: number): number => Math.ceil((p * size.w) / view.cols) * size.scale;
    const edgeY = (p: number): number => Math.ceil((p * size.h) / view.rows) * size.scale;
    // Only the hairlines: the cover itself went down cell by cell above, from
    // the kernel's own `terrain`, so a covered piece and its cells can never
    // disagree about where the piece ends.
    ctx.strokeStyle = COVER_EDGE;
    ctx.lineWidth = 1;
    for (let py = 0; py < view.rows; py++) {
      for (let px = 0; px < view.cols; px++) {
        const x = edgeX(px);
        const y = edgeY(py);
        const w = edgeX(px + 1) - x;
        const h = edgeY(py + 1) - y;
        if (w <= 0 || h <= 0) continue;
        ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
      }
    }

    // The X, once every obelisk has been stood on.
    if (view.cell >= 0 && view.x >= 0) {
      const cx = (view.x + 0.5) * size.scale;
      const cy = (view.y + 0.5) * size.scale;
      const r = Math.max(6, size.scale * 3);
      for (const [colour, width] of [[MARK_SHADOW, 5], [MARK, 3]] as const) {
        ctx.strokeStyle = colour;
        ctx.lineWidth = width;
        ctx.beginPath();
        ctx.moveTo(cx - r, cy - r); ctx.lineTo(cx + r, cy + r);
        ctx.moveTo(cx + r, cy - r); ctx.lineTo(cx - r, cy + r);
        ctx.stroke();
      }
    }
  }, [size, view]);

  const solved = view.cell >= 0;
  const subtitle = view.total === 0
    ? "There are no obelisks on this map"
    : solved
      ? `Every obelisk stood on — dig at ${String(view.x)}, ${String(view.y)}`
      : `${String(view.seen)} of ${String(view.total)} obelisks stood on`;

  const fit = usePhoneSheetFit();
  return (
    <div className={`hs-screen ${fit.screenClass}`.trim()} ref={fit.screenRef}
      style={fit.style} role="region" aria-label="Puzzle Map">
      <ScreenStyles />

      <div className="hs-phone-sheet" ref={fit.sheetRef}>
      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title">Puzzle Map</span>
        <span className="hs-screen__sub hs-muted">{subtitle}</span>
        <span className="hs-spacer" />
        {solved && props.onGoTo !== undefined ? (
          <button
            type="button"
            className="hs-plate hs-plate--sm"
            onClick={() => { props.onGoTo?.(view.x, view.y, 0); }}
          >
            Go to the site
          </button>
        ) : null}
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Close</button>
      </div>

      <div
        className="hs-screen__body"
        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}
      >
        <div className="hs-card hs-stone" style={{ padding: "10px" }}>
          <canvas
            ref={canvasRef}
            style={{
              display: "block",
              width: `${String(size.drawnW)}px`,
              height: `${String(size.drawnH)}px`,
              imageRendering: "pixelated",
            }}
            aria-label="The uncovered part of the map"
          />
        </div>
        <div className="hs-tiny hs-muted" style={{ maxWidth: 620, textAlign: "center" }}>
          {view.total === 0
            ? "Nothing here will ever be uncovered. The Grail is still buried somewhere,"
              + " and only a spade will find it."
            : solved
              ? "The ground the pieces were hiding. Stand a hero on the marked cell with a"
                + " full day's movement in hand and dig."
              : "Each obelisk a hero stands on takes another piece off. The ground under the"
                + " last piece is where the Grail is buried."}
        </div>
      </div>
      </div>
    </div>
  );
}
